import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ExternalLink, RefreshCcw } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge, CustomerLink, DataTable, Drawer, KeyRow, OrderLink, Panel, Select, money, stamp, statusTone, useTable } from "./ui";

const PaymentDrawer = ({ id, onClose }) => {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/payments/${id}`);
      setD(data);
    } catch (e) {
      setMsg(errText(e));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setD(null); load(); }, [id]);

  const refresh = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/admin/payments/${id}/refresh`);
      setMsg(`Read back from ${data.gateway}: ${data.live.status || (data.live.complete ? "complete" : "open")}`);
      await load();
    } catch (e) {
      setMsg(errText(e));
    }
    setBusy(false);
  };

  if (!d) return <Drawer testid="payment-drawer" title="Payment" onClose={onClose}><p className="text-sm text-muted">Loading…</p></Drawer>;
  const p = d.payment;

  return (
    <Drawer testid="payment-drawer" title={p.order_number || p.session_id} onClose={onClose}
            subtitle={`${p.gateway} · ${p.mode} · ${p.currency} ${p.amount}`}
            actions={<button className="text-xs flex items-center gap-1" data-testid="payment-refresh" disabled={busy} onClick={refresh}>
              <RefreshCcw size={13} className={busy ? "animate-spin" : ""} /> Refresh from gateway
            </button>}>
      {msg ? <p className="text-sm text-brass" data-testid="payment-drawer-message">{msg}</p> : null}
      <Panel title="Transaction" testid="payment-transaction">
        <KeyRow label="Gateway"><Badge tone="ink">{p.gateway}</Badge> {p.mode}</KeyRow>
        <KeyRow label="Session id">{p.session_id}</KeyRow>
        <KeyRow label="Gateway payment id">{p.payment_id || "—"}</KeyRow>
        <KeyRow label="Status">{p.status} · payment {p.payment_status}</KeyRow>
        <KeyRow label="Capture">{p.capture_mode} · hold {p.payment_state}</KeyRow>
        <KeyRow label="Amount">{p.currency} {p.amount}</KeyRow>
        <KeyRow label="Buyer"><CustomerLink email={p.buyer_email} /></KeyRow>
        <KeyRow label="Started">{stamp(p.created_at)}</KeyRow>
        {d.raw?.gateway_snapshot ? (
          <KeyRow label="Last gateway read" testid="payment-snapshot">
            {d.raw.gateway_snapshot.status || (d.raw.gateway_snapshot.complete ? "complete" : "open")} · {stamp(d.raw.gateway_snapshot.read_at)}
          </KeyRow>
        ) : null}
        {p.dashboard_url ? (
          <a className="btn-ghost text-xs inline-flex items-center gap-1" href={p.dashboard_url} target="_blank" rel="noreferrer" data-testid="payment-dashboard-link">
            Open in {p.gateway} <ExternalLink size={12} />
          </a>
        ) : null}
      </Panel>

      <Panel title="Orders paid by this transaction" testid="payment-orders">
        {d.orders.map((o) => (
          <p key={o.id} className="text-sm border-b border-brass-light py-1.5 flex flex-wrap gap-2 items-center">
            <OrderLink number={o.order_number} id={o.id} />
            <Badge tone={statusTone(o.status)}>{o.status}</Badge>
            <span className="text-xs text-muted">{o.city}</span>
            <span className="ms-auto tabular text-xs">
              Customer total {money(o.total_mad)} MAD · Partner payout {money(o.payout_mad)} MAD · Card fee {money(o.gateway_fee_mad)} MAD
            </span>
          </p>
        ))}
      </Panel>

      <Panel title="Webhook events" testid="payment-events">
        {d.events?.length ? d.events.map((e, i) => (
          <p key={`${e.received_at}-${e.type || e.event_type}-${i}`} className="text-xs border-b border-brass-light py-1 flex gap-2">
            <span className="tabular text-muted">{stamp(e.received_at)}</span><span>{e.type || e.event_type}</span>
            <span className="ms-auto text-muted">{e.status || ""}</span>
          </p>
        )) : <p className="text-sm text-muted">The gateway has not called us about this one.</p>}
      </Panel>
    </Drawer>
  );
};

export default function PaymentsPage() {
  const table = useTable("/admin/tables/payments");
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("payment");
  const close = () => { const n = Object.fromEntries(sp.entries()); delete n.payment; setSp(n, { replace: true }); };
  const open = (id) => setSp({ ...Object.fromEntries(sp.entries()), payment: id }, { replace: true });

  const columns = [
    { key: "created_at", label: "Date", sortable: true, render: (r) => <span className="whitespace-nowrap text-xs">{stamp(r.created_at)}</span> },
    { key: "order_number", label: "Order", sortable: true, render: (r) => (r.order_id ? <OrderLink number={r.order_number} id={r.order_id} /> : r.order_number) },
    { key: "buyer_email", label: "Buyer", render: (r) => <span className="text-xs"><CustomerLink email={r.buyer_email} /></span> },
    { key: "gateway", label: "Gateway", render: (r) => <Badge tone="ink">{r.gateway}</Badge> },
    { key: "amount", label: "Amount", sortable: true, render: (r) => <span className="tabular whitespace-nowrap">{r.currency} {r.amount}</span> },
    { key: "payment_status", label: "Payment", sortable: true, render: (r) => <Badge tone={r.payment_status === "paid" ? "good" : "warn"}>{r.payment_status}</Badge> },
    { key: "capture_mode", label: "Capture", render: (r) => <span className="text-xs">{r.capture_mode} · {r.payment_state}</span> },
    { key: "gateway_fee_mad", label: "Card fee", render: (r) => <span className="tabular whitespace-nowrap">{money(r.gateway_fee_mad)} MAD</span> },
    { key: "payment_id", label: "Gateway id", render: (r) => <span className="text-[11px] text-muted break-all">{r.payment_id || "—"}</span> },
    { key: "actions", label: "", render: (r) => (
      <button className="btn-ghost text-[11px]" data-testid={`payment-open-${r.id}`} onClick={() => open(r.id)}>Open</button>
    ) },
  ];

  const filters = (
    <>
      <Select testid="payments-filter-gateway" placeholder="Any gateway" value={table.params.gateway} onChange={(v) => table.set({ gateway: v })}
              options={["stripe", "tryb"]} />
      <Select testid="payments-filter-status" placeholder="Any payment state" value={table.params.payment_status} onChange={(v) => table.set({ payment_status: v })}
              options={["paid", "pending", "failed"]} />
      <Select testid="payments-filter-capture" placeholder="Any capture" value={table.params.capture_mode} onChange={(v) => table.set({ capture_mode: v })}
              options={["immediate", "preauth"]} />
      <input type="date" className="ctl" data-testid="payments-filter-from" value={table.params.date_from || ""} onChange={(e) => table.set({ date_from: e.target.value })} />
      <input type="date" className="ctl" data-testid="payments-filter-to" value={table.params.date_to || ""} onChange={(e) => table.set({ date_to: e.target.value })} />
    </>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted" data-testid="payments-intro">
        Stripe and TRYB Billing in one place — no need to open either dashboard. Taking payments through{" "}
        <b className="text-ink">{table.data?.active_gateway || "…"}</b> right now.
      </p>
      <DataTable table={table} columns={columns} testid="payments-table" name="payments" filters={filters}
                 searchPlaceholder="Order or session id…" empty="No transaction matches those filters." />
      {openId ? <PaymentDrawer id={openId} onClose={close} /> : null}
    </div>
  );
}
